import {ChangeEvent, useState} from 'react';
import {CircularProgress, Grid, TextField} from "@material-ui/core";
import {Autocomplete} from '@material-ui/lab';
import {isNull, uniqBy} from "lodash-es";
import {useTranslation} from "react-i18next";
import i18nNamespaces from "../../../../const/i18nNamespaces";
import {useAppDispatch, useAppSelector} from "../../../../hooks/customReduxHooks";
import {
    selectMedicDetails,
    selectNewMedicLanguages,
    selectTagsStatus,
    setNewMedicLanguages,
} from "../../../../store/medicProfileSlice";
import {isNotNull} from "../../../../utils/lang";
import {CustomButton} from '../../../index';
import useStyles from './AddLanguageStyles';
import {LanguageDto} from "../../../../types/language";
import {fetchLanguages, selectLanguages} from "../../../../store/languageSlice";

const AddLanguage = () => {
    const classes = useStyles();
    const {t: commonT} = useTranslation(i18nNamespaces.COMMON);
    const {t: patientPackagesT} = useTranslation(i18nNamespaces.PATIENT_PACKAGES);
    const dispatch = useAppDispatch();

    const languages = useAppSelector(selectLanguages);
    const tagsStatus = useAppSelector(selectTagsStatus);

    const medicLanguages = useAppSelector(selectMedicDetails)?.languages;
    const newMedicLanguages = useAppSelector(selectNewMedicLanguages);

    const [open, setOpen] = useState(false);
    const [selectedOption, setSelectedOption] = useState<LanguageDto>(null);

    const autocompleteOpenHandler = () => {
        setOpen(true);
        const tagsNotInitialized = isNull(languages);
        if (tagsNotInitialized) {
            dispatch(fetchLanguages());
        }
    }

    const autocompleteChangeHandler = (_: ChangeEvent<unknown>, value: string | LanguageDto , reason: string) => {
        if (reason === 'select-option') {
            setSelectedOption(value as LanguageDto);
        }
        if (reason === 'clear') {
            setSelectedOption(null);
        }
    }

    const addTagButtonClickHandler = () => {
        const medicLangsInUse = isNotNull(newMedicLanguages)
            ? newMedicLanguages
            : medicLanguages;
        const newMedicTagsUniq = uniqBy<LanguageDto>(
            [...medicLangsInUse, selectedOption],
            'id'
        );
        dispatch(
            setNewMedicLanguages(
                newMedicTagsUniq
            )
        );
        setSelectedOption(null);
    }

    const loading = open && tagsStatus === 'loading';

    return (
        <Grid container>
            <Grid
                className={classes.autocompleteWrapper}
                item
            >
                <Autocomplete
                    classes={{
                        popupIndicator: classes.autocompleteArrow,
                        inputRoot: classes.autocompleteRoot
                    }}
                    open={open}
                    onOpen={autocompleteOpenHandler}
                    onClose={() => {
                        setOpen(false);
                    }}
                    value={selectedOption}
                    options={languages || []}
                    getOptionLabel={(option) => option.code}
                    loading={loading}
                    onChange={autocompleteChangeHandler}
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            label={
                                <span className={classes.autocompleteLabel}>
                                    {commonT('selectOrSearch')}
                                </span>
                            }
                            variant="outlined"
                            InputProps={{
                                ...params.InputProps,
                                endAdornment: (
                                    <>
                                        {
                                            loading
                                                ? <CircularProgress color="primary" size={20} />
                                                : null
                                        }
                                        {params.InputProps.endAdornment}
                                    </>
                                ),
                            }}
                        />
                    )}
                />
            </Grid>
            <Grid
                item
                className={classes.addTagButtonWrapper}
            >
                <CustomButton
                    variant={'outlined'}
                    color={'secondary'}
                    onClick={addTagButtonClickHandler}
                    disabled={!selectedOption}
                >
                    {patientPackagesT('add')}
                </CustomButton>
            </Grid>
        </Grid>
    )
}

export default AddLanguage;
